<template>
  <div class="q-chart-card">
    <div class="q-chart-head">
      <div>
        <h6 class="mb-0">{{ title }}</h6>
        <p v-if="subtitle" class="text-sm opacity-7 mb-0">{{ subtitle }}</p>
      </div>

      <div v-if="rightText" class="q-chart-right">
        <span class="text-xs opacity-7">{{ rightText }}</span>
      </div>
    </div>

    <div class="q-chart-body">
      <canvas ref="canvas" :id="id" class="q-chart-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "QuantyBarChart",
  props: {
    id: { type: String, required: true },

    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    rightText: { type: String, default: "" },

    labels: { type: Array, default: () => [] },
    data: { type: Array, default: () => [] },

    // texto tooltip
    tooltipPrefix: { type: String, default: "" },  // ej "€ "
    tooltipSuffix: { type: String, default: "" },  // ej " ventas"
    // formato de números
    valueFormat: { type: String, default: "number" }, // "number" | "money"

    // opciones
    horizontal:    { type: Boolean, default: true },
    maxItems:      { type: Number,  default: 8 },
    useFlags:      { type: Boolean, default: true },
    integerTicks:  { type: Boolean, default: false },
    maxBarSize:    { type: Number,  default: 22 },
  },
  data() {
    return {
      chartInstance: null,
    };
  },
 computed: {
  safeLabels() {
    const sliced = (this.labels || []).slice(0, this.maxItems);
    return this.useFlags ? sliced.map(c => this.countryToFlag(c)) : sliced;
  },
  safeData() {
    return (this.data || []).slice(0, this.maxItems);
  },
},
  watch: {
    labels() { this.renderChart(); },
    data()   { this.renderChart(); },
  },
  mounted() {
    this.$nextTick(() => requestAnimationFrame(() => this.renderChart()));
  },
  beforeUnmount() {
    this.destroyChart();
  },
  methods: {
    countryToFlag(code) {
        const cc = String(code || "").trim().toUpperCase();
        if (!/^[A-Z]{2}$/.test(cc)) return "🏳️"; // desconocido
        const A = 0x1F1E6;
        return String.fromCodePoint(
            A + (cc.charCodeAt(0) - 65),
            A + (cc.charCodeAt(1) - 65)
        );
    },
    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
    fmtValue(v) {
      const n = Number(v || 0);
      if (this.valueFormat === "money") {
        return n.toLocaleString("es-ES", { style: "currency", currency: "EUR" });
      }
      return n.toLocaleString("es-ES");
    },
    renderChart() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      this.destroyChart();

      const indexAxis = this.horizontal ? "y" : "x";

      this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.safeLabels,
          datasets: [
            {
              label: this.title || "",
              data: this.safeData,
              borderWidth: 0,
              borderRadius: 10,
              borderSkipped: false,
              maxBarThickness: this.maxBarSize,
              backgroundColor: "rgba(245, 166, 35, .85)",
              hoverBackgroundColor: "rgba(246, 174, 53, .95)",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  // En barras horizontales (indexAxis="y") el valor está en parsed.x
                  // En barras verticales (indexAxis="x") el valor está en parsed.y
                  const valueAxis = this.horizontal ? "x" : "y";
                  const val = ctx.parsed?.[valueAxis] ?? ctx.raw ?? 0;
                  const txt = this.fmtValue(val);
                  return `${this.tooltipPrefix}${txt}${this.tooltipSuffix}`;
                },
              },
            },
          },
          indexAxis,
          scales: {
            x: {
              grid: { display: false, drawBorder: false },
              ticks: {
                color: "#7c8ba1",
                font: { size: 12, family: "Inter" },
                ...(this.integerTicks && !this.horizontal ? { precision: 0, stepSize: 1 } : {}),
              },
            },
            y: {
              grid: { display: false, drawBorder: false },
              ticks: {
                color: "#7c8ba1",
                font: { size: 12, family: "Inter" },
                ...(this.integerTicks && this.horizontal  ? { precision: 0, stepSize: 1 } : {}),
                ...(this.integerTicks && !this.horizontal ? { precision: 0, stepSize: 1 } : {}),
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.q-chart-card{
  background: var(--q-card);
  border: 1px solid var(--q-border);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.q-chart-head{
  padding: 16px 18px 10px 18px;
  display:flex;
  align-items:flex-end;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--q-border);
}

.q-chart-body{
  padding: 10px 16px 16px 16px;
  height: 260px;
}

.q-chart-right{
  display:flex;
  align-items:center;
  gap: 8px;
}

.q-chart-canvas{
  width: 100% !important;
  height: 100% !important;
}
</style>